/**
 * DirectionContext — keeps i18next, document.lang, and document.dir
 * in sync. Anything in OVACS that needs to know the current language
 * or text direction reads from this single context.
 *
 * `lang`  → 'en' | 'ar'
 * `dir`   → 'ltr' | 'rtl'  (derived; never set directly)
 * `isRtl` → boolean        (derived convenience)
 *
 * Storage: localStorage('ovacs.lang') = 'en' | 'ar'
 * Bootstrapped on first paint by the inline script in index.html so
 * there's no flicker — once React mounts, this context owns the value.
 *
 * API:
 *   const { lang, dir, isRtl, setLang, toggle } = useDirection()
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const STORAGE_KEY = 'ovacs.lang'
const RTL_LANGS = new Set(['ar', 'he', 'fa', 'ur'])

function readStoredLang() {
  // Default to ENGLISH unless the user has explicitly chosen otherwise.
  // Browser locale is intentionally ignored — owner spec was "English
  // default, user can switch via the profile dropdown."
  if (typeof window === 'undefined') return 'en'
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === 'en' || stored === 'ar') return stored
  return 'en'
}

function dirFor(lang) {
  return RTL_LANGS.has(lang) ? 'rtl' : 'ltr'
}

const DirectionContext = createContext({
  lang: 'en',
  dir: 'ltr',
  isRtl: false,
  setLang: () => {},
  toggle: () => {},
})

export function DirectionProvider({ children }) {
  const { i18n } = useTranslation()
  const [lang, setLangState] = useState(readStoredLang)

  // Apply lang + dir to <html> whenever the choice changes. The inline
  // script in index.html handles first paint; this effect handles every
  // subsequent change.
  useEffect(() => {
    if (typeof document === 'undefined') return
    document.documentElement.setAttribute('lang', lang)
    document.documentElement.setAttribute('dir', dirFor(lang))
    // Tell i18next about the change too — it sometimes lags behind if we
    // only set the attr, especially when the bootstrap script set the
    // initial lang before i18next finished init.
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang)
    }
  }, [lang, i18n])

  const setLang = useCallback((next) => {
    if (next !== 'en' && next !== 'ar') return
    setLangState(next)
    try { window.localStorage.setItem(STORAGE_KEY, next) } catch { /* quota / private mode */ }
  }, [])

  const toggle = useCallback(() => {
    setLang(lang === 'en' ? 'ar' : 'en')
  }, [lang, setLang])

  const value = useMemo(() => ({
    lang,
    dir: dirFor(lang),
    isRtl: dirFor(lang) === 'rtl',
    setLang,
    toggle,
  }), [lang, setLang, toggle])

  return <DirectionContext.Provider value={value}>{children}</DirectionContext.Provider>
}

export function useDirection() {
  return useContext(DirectionContext)
}

export default DirectionContext
